<template>
  <div class="order">
    <!-- 页头 + 已出货交易指标（与其他一级页面一致的布局） -->
    <div class="tx-head">
      <h1 class="vtm-page-head__title">{{ $t("menu.Order") }}</h1>
      <div class="vtm-page-head__sub">{{ $t("txHub.subtitle") }}</div>
    </div>
    <section class="tx-kpis">
      <stat-card
        icon="el-icon-money"
        :label="$t('txHub.kpiToday')"
        :value="$currency(kpi.todaySales || 0)"
        :hint="$t('txHub.deliveredCount', { n: kpiCounts.today })"
      ></stat-card>
      <stat-card
        tone="info"
        icon="el-icon-time"
        :label="$t('txHub.kpiYesterday')"
        :value="$currency(kpi.yesterdaySales || 0)"
        :hint="$t('txHub.deliveredCount', { n: kpiCounts.yesterday })"
      ></stat-card>
      <stat-card
        tone="ok"
        icon="el-icon-data-line"
        :label="$t('txHub.kpiWeek')"
        :value="$currency(kpi.weekSales || 0)"
        :hint="$t('txHub.deliveredCount', { n: kpiCounts.week })"
      ></stat-card>
    </section>
    <search-order
      v-on:resetSearch="resetSearch"
      v-on:onSubmit="onSubmit"
      :payStateList="payStateList"
      :payTypeList="payTypeList"
      :search="search"
      :dateTime="dateTime"
    >
      <template v-slot:extra>
        <column-manager :columns="columnConfig" storage-key="order_extra_columns" @change="onColumnsChange" />
      </template>
    </search-order>

    <div class="vue-padding radius">
      <el-table
        v-loading="loading"
        :empty-text="$t('common.noData')"
        :element-loading-text="$t('common.loading')"
        :key="tableKey"
        :data="orderList"
        style="width: 100%"
        max-height="720"
        :border="false"
        row-class-name="order-row"
        @row-click="openDetail"
        @sort-change="sortChange"
      >
        <!-- 商品：图片打头 + 名称（特征字段）；多商品订单展示每件图片 -->
        <el-table-column :label="$t('order.goodsName')" min-width="280" prop="goodsName" sortable="custom">
          <template slot-scope="scope">
            <!-- 多商品：逐件缩略图 + 「多件商品」标识 -->
            <div v-if="itemCount(scope.row) > 1" class="order-prod">
              <div class="order-prod__imgs">
                <el-image
                  v-for="(g, i) in itemImages(scope.row)"
                  :key="i"
                  class="order-prod__img order-prod__img--multi"
                  :src="g"
                  fit="contain"
                >
                  <div slot="error" class="order-prod__ph"><i class="el-icon-goods"></i></div>
                  <div slot="placeholder" class="order-prod__ph"><i class="el-icon-goods"></i></div>
                </el-image>
                <span v-if="itemCount(scope.row) > itemImages(scope.row).length" class="order-prod__count"
                  >+{{ itemCount(scope.row) - itemImages(scope.row).length }}</span
                >
              </div>
              <div class="order-prod__meta">
                <span class="order-prod__name">{{ $t("order.multiItem") }}</span>
                <span class="order-prod__more">{{ $t("order.itemsCount", { n: itemCount(scope.row) }) }}</span>
              </div>
            </div>
            <!-- 单商品 -->
            <div v-else class="order-prod">
              <el-image class="order-prod__img" :src="goodsImage(scope.row)" fit="contain">
                <div slot="error" class="order-prod__ph"><i class="el-icon-goods"></i></div>
                <div slot="placeholder" class="order-prod__ph"><i class="el-icon-goods"></i></div>
              </el-image>
              <div class="order-prod__meta">
                <span class="order-prod__name">{{ productName(scope.row) }}</span>
              </div>
            </div>
          </template>
        </el-table-column>

        <!-- 设备：名称在上、编号在下 -->
        <el-table-column :label="$t('order.equipmentName')" min-width="180">
          <template slot-scope="scope">
            <div class="order-id">
              <span class="order-id__name">{{ scope.row.equipmentName || "—" }}</span>
              <span class="order-id__sub vtm-mono">{{ scope.row.equipmentNo }}</span>
            </div>
          </template>
        </el-table-column>

        <!-- 货道号：多商品订单逐件列出 -->
        <el-table-column :label="$t('inventoryLog.aisleNo')" width="90" align="center">
          <template slot-scope="scope">
            <span class="vtm-mono">{{ extraCell("aisleNo", scope.row) }}</span>
          </template>
        </el-table-column>

        <!-- 支付状态：颜色编码胶囊（特征字段） -->
        <el-table-column :label="$t('order.payState')" width="150" align="center" prop="payState">
          <template slot-scope="scope">
            <span class="vtm-pill" :class="'vtm-pill--' + payStateTone(scope.row)">{{ payState(scope.row) }}</span>
          </template>
        </el-table-column>

        <!-- 实付金额（特征字段） -->
        <el-table-column :label="$t('order.actualPrice')" width="140" align="right" prop="payablePrice" sortable="custom">
          <template slot-scope="scope">
            <span class="order-pay vtm-num">{{ $currency(scope.row.payablePrice) }}</span>
          </template>
        </el-table-column>

        <!-- 时间（不换行、不截断） -->
        <el-table-column :label="$t('order.createTime')" width="180" align="center" prop="buyTime" sortable="custom">
          <template slot-scope="scope">
            <span class="order-time vtm-num">{{ $formatDate(scope.row.buyTime) }}</span>
          </template>
        </el-table-column>

        <!-- 可选列（经典版有、新版默认隐藏）：在「列」按钮里勾选显示 -->
        <el-table-column
          v-for="col in extraColumns"
          :key="col.key"
          :label="col.label"
          :min-width="col.width"
          align="center"
          show-overflow-tooltip
        >
          <template slot-scope="scope">{{ extraCell(col.key, scope.row) }}</template>
        </el-table-column>

        <el-table-column width="44" align="center">
          <template slot-scope="scope">
            <i class="el-icon-arrow-right order-chev" @click.stop="openDetail(scope.row)"></i>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        background
        layout="total,sizes,prev, pager, next,jumper"
        :current-page="page.pageIndex"
        :total="page.totalSize"
        :page-size="page.pageSize"
        :page-sizes="[20, 50, 100, 200]"
        @size-change="sizeChange"
        @current-change="currentChange"
      ></el-pagination>
    </div>

    <!-- 交易详情飞出面板 -->
    <el-drawer :visible.sync="detailVisible" :with-header="false" size="520px" append-to-body>
      <div v-if="activeOrder" class="od">
        <div class="od__top">
          <el-image class="od__img" :src="goodsImage(activeOrder)" fit="contain">
            <div slot="error" class="order-prod__ph"><i class="el-icon-goods"></i></div>
            <div slot="placeholder" class="order-prod__ph"><i class="el-icon-goods"></i></div>
          </el-image>
          <div class="od__head">
            <div class="od__title">{{ productName(activeOrder) }}</div>
            <div class="od__ordno vtm-mono">{{ activeOrder.orderNumber }}</div>
            <span class="vtm-pill" :class="'vtm-pill--' + payStateTone(activeOrder)">{{ payState(activeOrder) }}</span>
          </div>
          <button class="od__close" type="button" @click="detailVisible = false"><i class="el-icon-close"></i></button>
        </div>

        <div class="od__scroll">
          <!-- 状态说明（非已出货订单）：帮助中心文案 + 深链《Understanding Transaction Status》 -->
          <div v-if="statusHelp(activeOrder)" class="od__status" :class="'od__status--' + payStateTone(activeOrder)">
            <div class="od__status-title">
              <i class="el-icon-info"></i>
              {{ $t("txHub.statusHelpTitle", { status: payState(activeOrder) }) }}
            </div>
            <div class="od__status-text">{{ statusHelp(activeOrder) }}</div>
            <a class="od__status-link" :href="statusArticleUrl" target="_blank" rel="noopener" @click.prevent="openStatusArticle">
              {{ $t("txHub.statusHelpLink") }} <i class="el-icon-arrow-right"></i>
            </a>
          </div>

          <!-- 金额概览 -->
          <div class="od__figures">
            <div class="od__fig od__fig--hero">
              <span class="od__fig-label">{{ $t("order.actualPrice") }}</span>
              <span class="od__fig-val vtm-num">{{ $currency(activeOrder.payablePrice) }}</span>
            </div>
            <div class="od__fig">
              <span class="od__fig-label">{{ $t("order.duePrice") }}</span>
              <span class="od__fig-val vtm-num">{{ $currency(activeOrder.payPrice) }}</span>
            </div>
            <div class="od__fig">
              <span class="od__fig-label">{{ $t("retainer.discountPrice") }}</span>
              <span class="od__fig-val vtm-num">{{ $currency(activeOrder.discountPrice) }}</span>
            </div>
          </div>

          <!-- 订单信息 -->
          <div class="od__section-title">{{ $t("order.orderInfo") }}</div>
          <div class="od__kv">
            <div class="od__kv-item"><span class="od__k">{{ $t("order.userName") }}</span><span class="od__v">{{ activeOrder.userName || "—" }}</span></div>
            <div class="od__kv-item"><span class="od__k">{{ $t("order.equipmentName") }}</span><span class="od__v">{{ activeOrder.equipmentName || "—" }}</span></div>
            <div class="od__kv-item"><span class="od__k">{{ $t("order.equipmentNo") }}</span><span class="od__v vtm-mono">{{ activeOrder.equipmentNo }}</span></div>
            <div class="od__kv-item"><span class="od__k">{{ $t("order.payType") }}</span><span class="od__v">{{ payType(activeOrder) }}</span></div>
            <div v-if="activeOrder.payType === '5'" class="od__kv-item"><span class="od__k">CardNo</span><span class="od__v vtm-mono">{{ activeOrder.icard }}</span></div>
            <div class="od__kv-item"><span class="od__k">{{ $t("editUserMachine.shuilv") }}</span><span class="od__v vtm-num">{{ activeOrder.vatRate }}%</span></div>
            <div class="od__kv-item"><span class="od__k">{{ $t("order.buyNum") }}</span><span class="od__v vtm-num">{{ activeOrder.buyNum }}</span></div>
            <div class="od__kv-item"><span class="od__k">{{ $t("order.createTime") }}</span><span class="od__v vtm-num">{{ $formatDate(activeOrder.buyTime) }}</span></div>
            <div class="od__kv-item"><span class="od__k">{{ $t("advertGroup.createTime") }}</span><span class="od__v vtm-num">{{ $formatDate(activeOrder.createTime) }}</span></div>
          </div>

          <!-- 商品明细 -->
          <template v-if="activeOrder.ordersGoods && activeOrder.ordersGoods.length > 0">
            <div class="od__section-title">{{ $t("order.lineItems") }}</div>
            <el-table :data="activeOrder.ordersGoods" size="mini" :border="false">
              <el-table-column prop="goodsName" :label="$t('order.goodsName')" min-width="140" show-overflow-tooltip></el-table-column>
              <el-table-column prop="aisleNo" :label="$t('inventoryLog.aisleNo')" width="70" align="center"><template slot-scope="s"><span class="vtm-mono">{{ s.row.aisleNo }}</span></template></el-table-column>
              <el-table-column :label="$t('order.sellingPrice')" width="80" align="right"><template slot-scope="s"><span class="vtm-num">{{ $currency(s.row.sellingPrice) }}</span></template></el-table-column>
              <el-table-column prop="buyNum" :label="$t('order.buyNum')" width="60" align="center"><template slot-scope="s"><span class="vtm-num">{{ s.row.buyNum }}</span></template></el-table-column>
              <el-table-column :label="$t('order.deliveryStatus')" width="110" align="center">
                <template slot-scope="s">
                  <span class="vtm-pill" :class="Number(s.row.successNum) > 0 ? 'vtm-pill--ok' : 'vtm-pill--warn'">{{ formatDeliveryStatus(s.row) }}</span>
                </template>
              </el-table-column>
            </el-table>
          </template>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
  import searchOrder from "_c/order/SearchOrder";
  import columnManager from "_c/machine/user/ColumnManager";
  import StatCard from "_c/common/StatCard.vue";
  import { showArticle } from "@/utils/intercomHelp";
  export default {
    name: "Order",
    components: {
      searchOrder,
      columnManager,
      StatCard
    },
    data() {
      const defaultEnd = new Date();
      const defaultStart = new Date(defaultEnd.getTime() - 7 * 24 * 60 * 60 * 1000);
      return {
        search: {
          goodsName: "",
          equipmentNo: "",
          equipmentName: "",
          orderNumber: "",
          trxNo: "",
          payState: "",
          payType: "",
          // 多选筛选（后端按 IN 匹配；单值字段保留给深链等老入口）
          payStates: [],
          equipmentNoList: [],
          equipmentNameList: [],

          payTypes: [],
          startTime: this.$timeUtil.formatTime(defaultStart),
          endTime: this.$timeUtil.formatTime(defaultEnd),
          userName: ""
        },
        dateTime: [defaultStart, defaultEnd],
        loading: false,
        page: {
          pageSize: 50,
          pageIndex: 1
        },
        payStateList: [],
        orderList: [],
        payTypeList: [],
        detailVisible: false,
        activeOrder: null,
        orderNumber: "",
        zone: this.$cookies.get("zone"),
        // 顶部指标（已出货交易）
        kpi: {},
        kpiCounts: { today: 0, yesterday: 0, week: 0 },
        // 可选列（经典版的列，默认隐藏；顺序与勾选保存在本地）
        tableKey: 0,
        columnConfig: [
          { key: "orderNumber", label: this.$t("order.orderNumber"), visible: false, width: 190 },
          { key: "userName", label: this.$t("order.userName"), visible: false, width: 180 },
          { key: "buyNum", label: this.$t("order.buyNum"), visible: false, width: 80 },
          { key: "costPrice", label: this.$t("order.price"), visible: false, width: 100 },
          { key: "sellingPrice", label: this.$t("order.sellingPrice"), visible: false, width: 110 },
          { key: "vatRate", label: this.$t("editUserMachine.shuilv"), visible: false, width: 90 },
          { key: "payPrice", label: this.$t("order.subtotal"), visible: false, width: 110 },
          { key: "discountPrice", label: this.$t("retainer.discountPrice"), visible: false, width: 110 },
          { key: "payType", label: this.$t("order.payType"), visible: false, width: 140 }
        ],
        // 交易状态说明文章（帮助中心）
        statusArticleUrl: "https://help.vapetm.com/en/articles/10560495-understanding-transaction-status-on-vapetm-software"
      };
    },
    computed: {
      extraColumns() {
        return this.columnConfig.filter(c => c.visible);
      }
    },
    mounted() {
      let _self = this;
      _self.listPayState();
      _self.currentChange();
      _self.listPayType();
      _self.loadKpis();
    },
    methods: {
      // 顶部指标：销售额（dashboard/sales：今日/昨日/近7日）+ 已出货交易笔数（order/page 计数）
      loadKpis() {
        this.$api
          .request(this.$url.Consoles.getSales, this.$method.get, {})
          .then(res => {
            if (res.code === 0 && res.data) {
              this.kpi = res.data;
            }
          })
          .catch(() => {});
        const fmt = t => this.$timeUtil.formatTime(t);
        const now = new Date();
        // 用日历日期构造边界（setDate），避免固定毫秒相减在夏令时切换日错位一小时
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        const yEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        yEnd.setSeconds(yEnd.getSeconds() - 1);
        // 金额来自后端 YEARWEEK（本周，周日起），笔数按同一区间统计，两者才对得上
        const weekStart = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
        const countDelivered = (startTime, endTime, key) => {
          this.$api
            .request(this.$url.Order.page, this.$method.get, { payState: 3, startTime, endTime, pageIndex: 1, pageSize: 1 })
            .then(res => {
              if (res.code === 0 && res.data) {
                this.kpiCounts[key] = parseInt(res.data.total) || 0;
              }
            })
            .catch(() => {});
        };
        countDelivered(fmt(todayStart), fmt(now), "today");
        countDelivered(fmt(yStart), fmt(yEnd), "yesterday");
        countDelivered(fmt(weekStart), fmt(now), "week");
      },
      // 商品图片：后端字段为 goodsUrl（商品链接），可能是逗号分隔的多图，取第一张
      firstImg(url) {
        if (!url) {
          return "";
        }
        return String(url).split(",")[0].trim();
      },
      goodsImage(row) {
        if (!row) {
          return "";
        }
        const direct = this.firstImg(row.goodsUrl);
        if (direct) {
          return direct;
        }
        if (Array.isArray(row.ordersGoods)) {
          for (const g of row.ordersGoods) {
            const u = this.firstImg(g && g.goodsUrl);
            if (u) {
              return u;
            }
          }
        }
        return "";
      },
      productName(row) {
        if (row.goodsName) {
          return row.goodsName;
        }
        if (Array.isArray(row.ordersGoods) && row.ordersGoods.length && row.ordersGoods[0].goodsName) {
          return row.ordersGoods[0].goodsName;
        }
        return "—";
      },
      itemCount(row) {
        return Array.isArray(row.ordersGoods) ? row.ordersGoods.length : 0;
      },
      // 多商品订单：每件商品的缩略图（最多 4 张，其余以 +N 计数）
      itemImages(row) {
        if (!Array.isArray(row.ordersGoods)) {
          return [];
        }
        return row.ordersGoods.slice(0, 4).map(g => this.firstImg(g && g.goodsUrl));
      },
      // 支付状态 → 颜色：已出货(3)=绿，已退款(-2)=红，其余=灰
      // 应用内打开状态说明文章（消息器不可用时回退网页）
      openStatusArticle() {
        showArticle(10560495, this.statusArticleUrl);
      },
      // 非已出货状态的解释文案（口径来源：帮助中心《Understanding Transaction Status》）
      statusHelp(row) {
        const KEY = { "0": "unpaid", "1": "suspending", "2": "suspending", "-1": "refunding", "-2": "refunded", "-3": "expired", "-4": "cancelled" };
        const k = KEY[String(row.payState)];
        return k ? this.$t("txHub.status." + k) : "";
      },
      // payState: 0 待支付 · 1 已支付 · 2 已接单 · 3 已出货 · -1 待退款 · -2 已退款 · -3 已失效 · -4 已取消
      payStateTone(row) {
        const v = String(row.payState);
        if (v === "3") {
          return "ok";
        }
        if (v === "-2") {
          return "crit";
        }
        return "off";
      },
      openDetail(row) {
        this.activeOrder = row;
        this.detailVisible = true;
      },
      sortChange(column) {
        if (column.order === "ascending") {
          this.search.order = "asc";
          this.search.sort = column.prop;
        } else if (column.order === "descending") {
          this.search.order = "desc";
          this.search.sort = column.prop;
        } else {
          delete this.search.sort;
          delete this.search.order;
        }
        this.page.pageIndex = 1;
        this.currentChange();
      },
      revoke(row) {
        this.$confirm(this.$t("order.tipcontent"), this.$t("common.tip"), {
          confirmButtonText: this.$t("common.definite"),
          cancelButtonText: this.$t("common.cancel"),
          type: "warning"
        }).then(() => {
          this.$api
            .request(this.$url.Order.refundByOrderId, this.$method.post, {
              orderId: row.id
            })
            .then(res => {
              if (res.code === 0) {
                this.$notify({
                  title: this.$t("common.tip"),
                  message: res.msg,
                  type: "success"
                });
                this.currentChange(1);
              }
            });
        });
      },
      listPayState() {
        this.$api.dict("gp_pay_state").then(res => {
          if (res.code === 0) {
            this.payStateList = res.data;
          }
        });
      },
      listPayType() {
        this.$api.dict("gp_pay_type").then(res => {
          if (res.code === 0) {
            this.payTypeList = res.data;
          }
        });
      },
      sizeChange(pageSize) {
        this.page.pageSize = pageSize;
        this.currentChange();
      },
      currentChange(pageIndex) {
        this.loading = true;
        this.page.pageIndex = pageIndex ? pageIndex : 1;
        let obj = this.$cloud.objMerge(this.search, this.page);
        this.$api
          .request(this.$url.Order.page, this.$method.get, obj)
          .then(res => {
            if (res.code === 0) {
              this.orderList = res.data.records;
              this.page.totalSize = parseInt(res.data.total);
            }
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
          });
      },
      // 格式化订单状态内容
      payState(row) {
        return this.$cloud.getDictLabel(this.payStateList, row.payState);
      },
      // 格式化付款方式内容
      payType(row) {
        return this.$cloud.getDictLabel(this.payTypeList, row.payType);
      },
      // 格式化配送状态内容
      formatDeliveryStatus(item) {
        if (!item) {
          return this.$t("order.notDelivered");
        }
        const successNum = Number(item.successNum) || 0;
        if (successNum > 0) {
          return this.$t("order.delivered");
        } else {
          return this.$t("order.notDelivered");
        }
      },
      onColumnsChange(cols) {
        this.columnConfig = cols;
        this.tableKey++;
      },
      // 可选列取值：多商品订单按商品逐个列出（逗号分隔）
      extraCell(key, row) {
        const money = v => (v === null || v === undefined || v === "" ? "—" : this.$currency(v));
        const items = Array.isArray(row.ordersGoods) && row.ordersGoods.length ? row.ordersGoods : null;
        const perItem = fmt => (items ? items.map(g => fmt(g)).join(", ") : fmt(row));
        switch (key) {
          case "payType":
            return this.payType(row);
          case "payPrice":
          case "discountPrice":
            return money(row[key]);
          case "costPrice":
          case "sellingPrice":
            return perItem(g => money(g[key]));
          case "vatRate":
            return perItem(g => (g.vatRate === null || g.vatRate === undefined ? "—" : g.vatRate + "%"));
          case "aisleNo":
          case "buyNum":
            return perItem(g => (g[key] === null || g[key] === undefined ? "—" : g[key]));
          default:
            return row[key] || "—";
        }
      },
      onSubmit(data) {
        this.search = data;
        this.dateTime = [new Date(data.startTime), new Date(data.endTime)];
        this.currentChange();
      },
      resetSearch(data) {
        this.search = data;
        this.currentChange();
      }
    }
  };
</script>

<style lang="scss">
  .order {
    .order-row {
      cursor: pointer;
    }
  }
</style>
<style scoped lang="scss">
  // 页头 + 指标行（与其他一级页面一致）
  .tx-head {
    padding: 16px 16px 0;
  }
  .tx-kpis {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
    padding: 10px 16px 12px;
  }

  // 商品单元格：图片打头
  .order-prod {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .order-prod__img {
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    border-radius: var(--vtm-r-sm);
    background: var(--vtm-well);
    overflow: hidden;
  }

  .order-prod__ph {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: var(--vtm-ink-3);
    font-size: 20px;
    background: var(--vtm-well);
  }

  .order-prod__meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .order-prod__name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--vtm-ink);
    white-space: normal;
    word-break: break-word;
    line-height: 1.35;
  }

  .order-prod__more {
    font-size: 11.5px;
    color: var(--vtm-ink-3);
    margin-top: 2px;
  }

  // 多商品：缩略图行
  .order-prod__imgs {
    display: flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }

  .order-prod__img--multi {
    width: 34px;
    height: 34px;
  }

  .order-prod__count {
    font-size: 11.5px;
    font-weight: 600;
    color: var(--vtm-ink-2);
    padding: 0 4px;
  }

  // 时间：不换行、不截断
  .order-time {
    white-space: nowrap;
  }

  // 身份单元格：名称在上、编号在下
  .order-id {
    display: flex;
    flex-direction: column;
    line-height: 1.35;
  }

  .order-id__name {
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-ink);
  }

  .order-id__sub {
    font-size: 11.5px;
    color: var(--vtm-ink-3);
  }

  .order-pay {
    font-size: 14px;
    font-weight: 700;
    color: var(--vtm-heading);
  }

  .order-chev {
    color: var(--vtm-ink-3);
    cursor: pointer;
    transition: color var(--vtm-speed) ease;

    &:hover {
      color: var(--vtm-mint-ink);
    }
  }

  // ---------- 交易详情飞出面板 ----------
  .od {
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .od__top {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 20px 20px 16px;
    border-bottom: 1px solid var(--vtm-line);
  }

  .od__img {
    width: 64px;
    height: 64px;
    flex-shrink: 0;
    border-radius: var(--vtm-r-md);
    background: var(--vtm-well);
    overflow: hidden;
  }

  .od__head {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .od__title {
    font-family: var(--vtm-display);
    font-size: 18px;
    font-weight: 600;
    color: var(--vtm-heading);
    line-height: 1.3;
    word-break: break-word;
  }

  .od__ordno {
    font-size: 12px;
    color: var(--vtm-ink-3);
  }

  .od__close {
    flex-shrink: 0;
    border: none;
    background: transparent;
    color: var(--vtm-ink-3);
    font-size: 18px;
    cursor: pointer;
    padding: 2px;

    &:hover {
      color: var(--vtm-crit);
    }
  }

  .od__scroll {
    flex: 1;
    overflow-y: auto;
    padding: 16px 20px 24px;
  }

  /* 状态说明卡：按状态色调着色（ok/warn/crit/info/off） */
  .od__status {
    margin-bottom: 14px;
    padding: 12px 14px;
    border-radius: var(--vtm-r-md);
    background: var(--vtm-well);
    border: 1px solid var(--vtm-line);

    &--ok {
      background: var(--vtm-ok-soft);
    }
    &--warn {
      background: var(--vtm-warn-soft);
    }
    &--crit {
      background: var(--vtm-crit-soft);
    }
    &--info {
      background: var(--vtm-info-soft);
    }
  }
  .od__status-title {
    margin-bottom: 5px;
    font-size: 13px;
    font-weight: 600;
    color: var(--vtm-heading);

    i {
      margin-right: 5px;
    }
  }
  .od__status-text {
    font-size: 12.5px;
    line-height: 1.55;
    color: var(--vtm-ink-2);
  }
  .od__status-link {
    display: inline-block;
    margin-top: 7px;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--vtm-mint-ink);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .od__figures {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-bottom: 20px;
  }

  .od__fig {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px;
    border-radius: var(--vtm-r-md);
    background: var(--vtm-well);
  }

  .od__fig--hero {
    background: var(--vtm-mint-soft);
  }

  .od__fig-label {
    font-size: 11px;
    color: var(--vtm-ink-2);
  }

  .od__fig-val {
    font-family: var(--vtm-display);
    font-size: 17px;
    font-weight: 600;
    color: var(--vtm-heading);
  }

  .od__fig--hero .od__fig-val {
    color: var(--vtm-mint-ink);
  }

  .od__section-title {
    font-size: 15px;
    font-weight: 500;
    color: var(--vtm-heading);
    padding-left: 8px;
    border-left: 5px solid var(--vtm-mint-ink);
    margin: 20px 0 12px;
  }

  .od__kv {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1px;
    background: var(--vtm-line);
    border: 1px solid var(--vtm-line);
    border-radius: var(--vtm-r-md);
    overflow: hidden;
  }

  .od__kv-item {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 10px 12px;
    background: var(--vtm-surface);
  }

  .od__k {
    font-size: 11px;
    color: var(--vtm-ink-3);
  }

  .od__v {
    font-size: 13px;
    color: var(--vtm-ink);
    word-break: break-word;
  }
</style>
